/**
 * VetNotesPanel - Vet Internal Notes Display and Input for Dashboard (Part 3)
 *
 * Displays Vet Internal Notes (EMRs) for the selected Pet:
 * - Shows notes sorted newest first
 * - Allows adding new notes inline
 * - Notes are per Pet (not per Device)
 *
 * Hospital-only, free-text, unchanged save path (`emrs`) — kept deliberately
 * separate from TreatmentPlanPanel.tsx's "Save & Send to Owner App" (vic/vetpo#14):
 * this panel's data never leaves the hospital side, so nothing here is read by
 * treatmentPlanApi.ts or included in a save-plan payload. Pinned by "Save & Send
 * never carries Vet Internal Notes content" in TreatmentPlanPanel.test.tsx.
 */

import AddIcon from '@mui/icons-material/Add'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LockIcon from '@mui/icons-material/Lock'
import NoteIcon from '@mui/icons-material/Note'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { parseISO } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
import { useCreate, useGetList, useNotify, useRefresh, useStore, useTranslate } from 'react-admin'
import { useDateFormatter } from '../../utils/localeDate'

export interface VetNotesPanelProps {
  petId?: string | number
  maxHeight?: number
  showAddForm?: boolean
}

const VetNotesEditor: React.FC<VetNotesPanelProps> = React.memo(
  ({ petId, maxHeight = 400, showAddForm = true }) => {
    const translate = useTranslate()
    const fmtDate = useDateFormatter()
    const refresh = useRefresh()
    const notify = useNotify()

    // Local state
    const [newNote, setNewNote] = useState('')
    const [includeInReport, setIncludeInReport] = useState(true)
    const [isAdding, setIsAdding] = useState(false)
    const [showForm, setShowForm] = useState(false)
    const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set())

    // One-shot "add note" intent from the dashboard's ADD NOTE button: open the
    // form and scroll to it on arrival, then clear so it doesn't re-fire.
    const rootRef = useRef<HTMLDivElement>(null)
    const [pendingScroll, setPendingScroll] = useState(false)
    const [addNoteIntent, setAddNoteIntent] = useStore<any>('addNoteForPetId')
    useEffect(() => {
      if (showAddForm && addNoteIntent != null && String(addNoteIntent) === String(petId)) {
        setShowForm(true)
        setAddNoteIntent(null)
        setPendingScroll(true)
      }
    }, [addNoteIntent, petId, showAddForm, setAddNoteIntent])
    // Scroll once the form is actually open. Re-scroll after a beat because the
    // heavy glucose charts above load late and shift the layout.
    useEffect(() => {
      if (!pendingScroll || !showForm) return
      const into = () => rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const t1 = setTimeout(into, 200)
      const t2 = setTimeout(() => {
        into()
        setPendingScroll(false)
      }, 1200)
      return () => {
        clearTimeout(t1)
        clearTimeout(t2)
      }
    }, [pendingScroll, showForm])

    // Create EMR mutation
    const [create, { isLoading: isCreating }] = useCreate()

    // Fetch EMRs for this pet
    const {
      data: notes = [],
      isLoading,
      error,
    } = useGetList(
      'emrs',
      {
        // Explicit depth (L-089): REST default 2 embeds every relation per row; the vet NAME is shown.
        meta: { params: { depth: 1 } },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'timestamp', order: 'DESC' }, // Newest first
        filter: petId ? { pet: petId } : {},
      },
      {
        enabled: !!petId,
      },
    )

    const handleAddNote = async () => {
      if (!newNote.trim() || !petId) return

      setIsAdding(true)
      try {
        await create(
          'emrs',
          {
            data: {
              pet: petId,
              notes: newNote.trim(),
              timestamp: new Date().toISOString(),
              includeInReport,
            },
          },
          {
            onSuccess: () => {
              setNewNote('')
              setIncludeInReport(true)
              setShowForm(false)
              refresh()
              notify('pos.notes.created_successfully', { type: 'success' })
            },
            onError: (error: any) => {
              notify(error.message || 'pos.notes.error_creating', { type: 'error' })
            },
          },
        )
      } finally {
        setIsAdding(false)
      }
    }

    const toggleNoteExpansion = (noteId: number) => {
      setExpandedNotes((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(noteId)) {
          newSet.delete(noteId)
        } else {
          newSet.add(noteId)
        }
        return newSet
      })
    }

    const formatNoteDate = (timestamp: string): string => {
      try {
        const date = parseISO(timestamp)
        return fmtDate(date, 'dateTime')
      } catch {
        return timestamp
      }
    }

    const truncateText = (text: string, maxLength: number = 150): string => {
      if (text.length <= maxLength) return text
      return `${text.substring(0, maxLength)}...`
    }

    if (!petId) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">{translate('pos.notes.select_pet_first')}</Typography>
        </Box>
      )
    }

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{translate('pos.dashboard.error_loading_data')}</Typography>
        </Box>
      )
    }

    return (
      <Box ref={rootRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header with Add button */}
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}
        >
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NoteIcon fontSize="small" />
            {translate('pos.notes.vet_notes')}
          </Typography>
          {showAddForm && (
            <Button
              size="small"
              startIcon={showForm ? <ExpandLessIcon /> : <AddIcon />}
              onClick={() => setShowForm(!showForm)}
              variant={showForm ? 'outlined' : 'contained'}
            >
              {showForm ? translate('ra.action.cancel') : translate('pos.notes.add_note')}
            </Button>
          )}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}
        >
          <LockIcon sx={{ fontSize: 14 }} />
          {translate('pos.notes.hospital_only_subtitle')}
        </Typography>

        {/* Add Note Form */}
        {showAddForm && (
          <Collapse in={showForm}>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={translate('pos.notes.enter_note')}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                disabled={isCreating}
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeInReport}
                    onChange={(e) => setIncludeInReport(e.target.checked)}
                    disabled={isCreating}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {translate('pos.notes.include_in_report')}
                  </Typography>
                }
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  onClick={() => {
                    setNewNote('')
                    setIncludeInReport(true)
                    setShowForm(false)
                  }}
                  disabled={isCreating}
                >
                  {translate('ra.action.cancel')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!newNote.trim() || isCreating}
                  startIcon={isCreating ? <CircularProgress size={16} /> : null}
                >
                  {translate('pos.notes.save_note')}
                </Button>
              </Box>
            </Paper>
          </Collapse>
        )}

        {/* Notes List */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            maxHeight: maxHeight,
          }}
        >
          {notes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NoteIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
              <Typography color="text.secondary">{translate('pos.notes.no_notes')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {translate('pos.notes.add_first_note')}
              </Typography>
            </Box>
          ) : (
            notes.map((note: any, index: number) => {
              const isExpanded = expandedNotes.has(note.id)
              const noteText = note.notes || ''
              const isLongNote = noteText.length > 150
              const isInternal = note.includeInReport === false

              return (
                <Paper
                  key={note.id}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderLeft: '4px solid',
                    borderColor: isInternal ? 'grey.400' : 'primary.main',
                    '&:hover': {
                      bgcolor: 'grey.50',
                    },
                  }}
                  elevation={0}
                  variant="outlined"
                >
                  {/* Note Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {formatNoteDate(note.timestamp)}
                      </Typography>
                      {isInternal && (
                        <Chip
                          icon={<LockIcon sx={{ fontSize: 12 }} />}
                          label={translate('pos.notes.internal_note')}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: 'grey.200',
                            color: 'text.secondary',
                            '& .MuiChip-icon': { color: 'text.secondary' },
                          }}
                        />
                      )}
                    </Box>
                    {note.vet && (
                      <Typography variant="caption" color="primary.main">
                        {typeof note.vet === 'object'
                          ? note.vet.name
                          : translate('pos.common.vet_fallback', { id: note.vet })}
                      </Typography>
                    )}
                  </Box>

                  {/* Note Content */}
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {isLongNote && !isExpanded ? truncateText(noteText) : noteText}
                  </Typography>

                  {/* Expand/Collapse for long notes */}
                  {isLongNote && (
                    <Button
                      size="small"
                      onClick={() => toggleNoteExpansion(note.id)}
                      sx={{ mt: 1, p: 0, minWidth: 'auto' }}
                    >
                      {isExpanded ? (
                        <>
                          <ExpandLessIcon fontSize="small" />
                          {translate('pos.notes.show_less')}
                        </>
                      ) : (
                        <>
                          <ExpandMoreIcon fontSize="small" />
                          {translate('pos.notes.show_more')}
                        </>
                      )}
                    </Button>
                  )}
                </Paper>
              )
            })
          )}
        </Box>

        {/* Note count */}
        {notes.length > 0 && (
          <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {translate('pos.notes.total_notes', { count: notes.length })}
            </Typography>
          </Box>
        )}
      </Box>
    )
  },
)

/**
 * Keyed by pet for the same reason as TreatmentPlanPanel: PetShow's route keeps
 * this panel mounted across /pets/A/show -> /pets/B/show, and a half-typed note
 * must not follow the vet onto another patient's record.
 */
export const VetNotesPanel: React.FC<VetNotesPanelProps> = React.memo(
  ({ petId: propPetId, ...rest }) => {
    const [storePetId] = useStore<any>('selectedPetId')
    const petId = propPetId ?? storePetId
    return <VetNotesEditor key={petId != null ? String(petId) : 'no-pet'} petId={petId} {...rest} />
  },
)

export default VetNotesPanel
